/**
 * FN-FE-14 — Beszerzés UI (RFQ, PO, áru-fogadás)
 */
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { get, post, patch, del } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState, Tabs, MoneyInput, NumInput } from '../../components/ui';
import { useFKeyBar } from '../../components/layout/FKeyBar';
import { useToast } from '../../components/ui/Toast';
import PartnerAutocomplete from '../../components/PartnerAutocomplete';
import ModulumBadge from '../../components/ui/ModulumBadge';

// ── Types ──

interface Rfq {
  id: string; rfq_number: string; title: string; status: string;
  deadline: string | null; supplier_name: string; created_at: string;
  gross_total: number;
}

interface PurchaseOrder {
  id: string; po_number: string; supplier_id: string; supplier_name: string;
  status: string; gross_total: number; expected_delivery: string | null;
  reference: string | null; created_at: string;
  network_order_id?: string | null;
  lines?: PoLine[];
}

interface PoLine {
  id: string; item_name: string; quantity: number; unit_price: number;
  vat_rate: number; gross_total: number; received_qty: number;
}

interface GoodsReceipt {
  id: string; gr_number: string; po_id: string; po_number: string;
  supplier_name: string; receipt_date: string; status: string;
  delivery_note_number: string | null; created_at: string;
}

interface Partner { id: string; name: string; }

// ── Constants ──

const RFQ_STATUS: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  draft: { label: 'Piszkozat', variant: 'default' },
  sent: { label: 'Elküldve', variant: 'info' },
  evaluating: { label: 'Értékelés', variant: 'warning' },
  closed: { label: 'Lezárva', variant: 'success' },
  cancelled: { label: 'Visszavonva', variant: 'error' },
};

const PO_STATUS: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  draft: { label: 'Piszkozat', variant: 'default' },
  sent: { label: 'Elküldve', variant: 'info' },
  confirmed: { label: 'Megrendelve', variant: 'info' },
  partially_received: { label: 'Részszállítás', variant: 'warning' },
  received: { label: 'Teljesítve', variant: 'success' },
  cancelled: { label: 'Visszavonva', variant: 'error' },
};

const GR_STATUS: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  draft: { label: 'Piszkozat', variant: 'default' },
  confirmed: { label: 'Rögzítve', variant: 'success' },
  cancelled: { label: 'Visszavonva', variant: 'error' },
};

function fmt(v: number) { return v ? new Intl.NumberFormat('hu-HU').format(Math.round(v)) + ' Ft' : '—'; }
function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
}
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Main Component ──

export default function ProcurementPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(0);

  // ── RFQ state ──
  const [rfqs, setRfqs] = useState<Rfq[]>([]);
  const [rfqLoading, setRfqLoading] = useState(true);
  const [showRfqModal, setShowRfqModal] = useState(false);

  // ── PO state ──
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [poLoading, setPoLoading] = useState(true);
  const [showPoModal, setShowPoModal] = useState(false);
  const [poDetail, setPoDetail] = useState<PurchaseOrder | null>(null);

  // ── GR state ──
  const [grs, setGrs] = useState<GoodsReceipt[]>([]);
  const [grLoading, setGrLoading] = useState(true);
  const [showGrModal, setShowGrModal] = useState(false);

  // ── Shared data ──
  const [partners, setPartners] = useState<Partner[]>([]);

  // ── Load functions ──

  const loadRfqs = useCallback(async () => {
    setRfqLoading(true);
    try {
      const data = await get<any>('/procurement/rfqs');
      setRfqs(Array.isArray(data) ? data : data?.data || []);
    } catch { /* silent */ }
    setRfqLoading(false);
  }, []);

  const loadPos = useCallback(async () => {
    setPoLoading(true);
    try {
      const data = await get<any>('/purchase-orders');
      setPos(Array.isArray(data) ? data : data?.data || []);
    } catch { /* silent */ }
    setPoLoading(false);
  }, []);

  const loadGrs = useCallback(async () => {
    setGrLoading(true);
    try {
      const data = await get<any>('/goods-receipts');
      setGrs(Array.isArray(data) ? data : data?.data || []);
    } catch { /* silent */ }
    setGrLoading(false);
  }, []);

  const loadPartners = useCallback(async () => {
    try {
      const data = await get<any>('/partners');
      setPartners(Array.isArray(data) ? data : data?.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadRfqs(); loadPos(); loadGrs(); loadPartners(); }, []);

  // ── FKeyBar ──

  useFKeyBar(activeTab === 0 ? [
    { key: 'F2', label: 'Új RFQ', action: () => setShowRfqModal(true) },
    { key: 'F5', label: 'Frissít', action: loadRfqs },
  ] : activeTab === 1 ? [
    { key: 'F2', label: 'Új PO', action: () => setShowPoModal(true) },
    { key: 'F5', label: 'Frissít', action: loadPos },
  ] : [
    { key: 'F2', label: 'Új fogadás', action: () => setShowGrModal(true) },
    { key: 'F5', label: 'Frissít', action: loadGrs },
  ]);

  return (
    <div>
      <PageHeader
        title="Beszerzés"
        description="Ajánlatkérések, megrendelések és áru-fogadás"
        actions={
          <div className="flex items-center gap-2">
            {activeTab === 0 && <Button onClick={() => setShowRfqModal(true)}>+ Új ajánlatkérés</Button>}
            {activeTab === 1 && <Button onClick={() => setShowPoModal(true)}>+ Új megrendelés</Button>}
            {activeTab === 2 && <Button onClick={() => setShowGrModal(true)}>+ Áru-fogadás</Button>}
          </div>
        }
      />

      <Tabs
        tabs={[
          {
            label: `Ajánlatkérések (${rfqs.length})`,
            content: <RfqTab rfqs={rfqs} loading={rfqLoading} onNew={() => setShowRfqModal(true)} toast={toast} onReload={loadRfqs} />,
          },
          {
            label: `Megrendelések (${pos.length})`,
            content: <PoTab pos={pos} loading={poLoading} onNew={() => setShowPoModal(true)} toast={toast} onReload={loadPos} onDetail={async (id) => {
              try { const d = await get<PurchaseOrder>(`/purchase-orders/${id}`); setPoDetail(d); } catch { toast('Betöltés sikertelen.', 'error'); }
            }} poDetail={poDetail} onCloseDetail={() => setPoDetail(null)} />,
          },
          {
            label: `Áru-fogadás (${grs.length})`,
            content: <GrTab grs={grs} loading={grLoading} onNew={() => setShowGrModal(true)} toast={toast} onReload={loadGrs} />,
          },
          {
            label: '🌐 Hálózati megrendelések',
            content: <NetworkOrdersTab />,
          },
        ]}
        defaultIndex={activeTab}
      />

      {/* RFQ Modal */}
      <RfqModal isOpen={showRfqModal} onClose={() => setShowRfqModal(false)} partners={partners}
        onSave={async (data) => {
          try { await post('/procurement/rfqs', data); toast('Ajánlatkérés létrehozva.', 'success'); setShowRfqModal(false); loadRfqs(); }
          catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
        }}
      />

      {/* PO Modal */}
      <PoModal isOpen={showPoModal} onClose={() => setShowPoModal(false)} partners={partners}
        onSave={async (data) => {
          try { await post('/procurement/orders', data); toast('Megrendelés létrehozva.', 'success'); setShowPoModal(false); loadPos(); }
          catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
        }}
      />

      {/* GR Modal */}
      <GrModal isOpen={showGrModal} onClose={() => setShowGrModal(false)} pos={pos.filter(p => p.status === 'confirmed' || p.status === 'partially_delivered')}
        onSave={async (poId, data) => {
          try { await post(`/purchase-orders/${poId}/receipts`, data); toast('Áru-fogadás rögzítve.', 'success'); setShowGrModal(false); loadGrs(); loadPos(); }
          catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
        }}
      />
    </div>
  );
}

// ── RFQ Tab ──

function RfqTab({ rfqs, loading, onNew, toast, onReload }: { rfqs: Rfq[]; loading: boolean; onNew: () => void; toast: (msg: string, v: any) => void; onReload: () => void }) {
  if (loading) return <div className="text-center py-12 text-gray-400">Betöltés...</div>;
  if (rfqs.length === 0) return <EmptyState title="Nincs ajánlatkérés" description="Hozd létre az első ajánlatkérésed!" action={<Button onClick={onNew}>+ Új ajánlatkérés</Button>} />;

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Sorszám</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Megnevezés</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Dátum</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Határidő</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Státusz</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Műveletek</th>
            </tr>
          </thead>
          <tbody>
            {rfqs.map(r => (
              <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-[#1a2540]">{r.rfq_number}</td>
                <td className="px-4 py-3 text-gray-600">{r.title || '—'}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(r.created_at)}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(r.deadline)}</td>
                <td className="px-4 py-3"><Badge variant={RFQ_STATUS[r.status]?.variant || 'default'}>{RFQ_STATUS[r.status]?.label || r.status}</Badge></td>
                <td className="px-4 py-3 text-right">
                  {r.status === 'draft' && (
                    <Button size="sm" variant="ghost" onClick={async () => {
                      try { await post(`/rfqs/${r.id}/send`, {}); toast('Ajánlatkérés elküldve.', 'success'); onReload(); } catch { toast('Küldés sikertelen.', 'error'); }
                    }}>Küldés</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── PO Tab ──

function PoTab({ pos, loading, onNew, toast, onReload, onDetail, poDetail, onCloseDetail }: {
  pos: PurchaseOrder[]; loading: boolean; onNew: () => void;
  toast: (msg: string, v: any) => void; onReload: () => void;
  onDetail: (id: string) => void; poDetail: PurchaseOrder | null; onCloseDetail: () => void;
}) {
  if (poDetail) {
    return (
      <div>
        <button onClick={onCloseDetail} className="text-sm text-gray-500 hover:text-[#1a2540] mb-4 inline-flex items-center gap-1">← Vissza a listához</button>
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{poDetail.po_number}</h3>
                <p className="text-sm text-gray-500">{poDetail.supplier_name} · {formatDate(poDetail.created_at)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={PO_STATUS[poDetail.status]?.variant || 'default'}>{PO_STATUS[poDetail.status]?.label || poDetail.status}</Badge>
                {poDetail.status === 'draft' && (
                  <Button size="sm" onClick={async () => {
                    try { await post(`/purchase-orders/${poDetail.id}/confirm`, {}); toast('Megrendelés megerősítve.', 'success'); onReload(); onCloseDetail(); }
                    catch { toast('Megerősítés sikertelen.', 'error'); }
                  }}>Megerősítés</Button>
                )}
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {poDetail.expected_delivery && <p className="text-sm text-gray-500 mb-3">Várt szállítás: {formatDate(poDetail.expected_delivery)}</p>}
            {poDetail.reference && <p className="text-sm text-gray-500 mb-3">Hivatkozás: {poDetail.reference}</p>}
            {poDetail.lines && poDetail.lines.length > 0 ? (
              <table className="w-full text-sm mt-3">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 font-semibold text-gray-600">Cikk</th>
                    <th className="text-right py-2 font-semibold text-gray-600">Mennyiség</th>
                    <th className="text-right py-2 font-semibold text-gray-600">Egységár</th>
                    <th className="text-right py-2 font-semibold text-gray-600">Bruttó</th>
                    <th className="text-right py-2 font-semibold text-gray-600">Fogadva</th>
                  </tr>
                </thead>
                <tbody>
                  {poDetail.lines.map((l, i) => (
                    <tr key={l.id || i} className="border-b border-gray-50">
                      <td className="py-2">{l.item_name}</td>
                      <td className="py-2 text-right">{l.quantity}</td>
                      <td className="py-2 text-right">{fmt(l.unit_price)}</td>
                      <td className="py-2 text-right font-semibold">{fmt(l.gross_total)}</td>
                      <td className="py-2 text-right">{l.received_qty || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-gray-400">Nincsenek tételek.</p>}
            <div className="mt-4 text-right">
              <span className="">{fmt(poDetail.gross_total)}</span>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (loading) return <div className="text-center py-12 text-gray-400">Betöltés...</div>;
  if (pos.length === 0) return <EmptyState title="Nincs megrendelés" description="Hozd létre az első beszerzési megrendelésed!" action={<Button onClick={onNew}>+ Új megrendelés</Button>} />;

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Sorszám</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Beszállító</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Dátum</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Bruttó</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Várt szállítás</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Státusz</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Műveletek</th>
            </tr>
          </thead>
          <tbody>
            {pos.map(po => (
              <tr key={po.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => onDetail(po.id)}>
                <td className="px-4 py-3 font-medium text-[#1a2540]">
                  {po.po_number}
                  {po.network_order_id && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--mist)', color: 'var(--orange)', fontWeight: 700, borderRadius: 4, padding: '1px 5px' }}>🌐 Hálózati</span>}
                </td>
                <td className="px-4 py-3 text-gray-600" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {po.network_order_id && <ModulumBadge />}{po.supplier_name}
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(po.created_at)}</td>
                <td className="px-4 py-3 text-right font-semibold">{fmt(po.gross_total)}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(po.expected_delivery)}</td>
                <td className="px-4 py-3"><Badge variant={PO_STATUS[po.status]?.variant || 'default'}>{PO_STATUS[po.status]?.label || po.status}</Badge></td>
                <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" onClick={() => onDetail(po.id)}>Részletek</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── GR Tab ──

function GrTab({ grs, loading, onNew, toast, onReload }: { grs: GoodsReceipt[]; loading: boolean; onNew: () => void; toast: (msg: string, v: any) => void; onReload: () => void }) {
  if (loading) return <div className="text-center py-12 text-gray-400">Betöltés...</div>;
  if (grs.length === 0) return <EmptyState title="Nincs áru-fogadás" description="Rögzítsd az első beérkező szállítmányt!" action={<Button onClick={onNew}>+ Áru-fogadás</Button>} />;

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Sorszám</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">PO</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Beszállító</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Fogadás dátuma</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Státusz</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Műveletek</th>
            </tr>
          </thead>
          <tbody>
            {grs.map(gr => (
              <tr key={gr.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-[#1a2540]">{gr.gr_number}</td>
                <td className="px-4 py-3 text-gray-600">{gr.po_number}</td>
                <td className="px-4 py-3 text-gray-600">{gr.supplier_name}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(gr.receipt_date)}</td>
                <td className="px-4 py-3"><Badge variant={GR_STATUS[gr.status]?.variant || 'default'}>{GR_STATUS[gr.status]?.label || gr.status}</Badge></td>
                <td className="px-4 py-3 text-right">
                  {gr.status === 'draft' && (
                    <Button size="sm" variant="ghost" onClick={async () => {
                      try { await post(`/goods-receipts/${gr.id}/confirm`, {}); toast('Áru-fogadás rögzítve.', 'success'); onReload(); } catch { toast('Rögzítés sikertelen.', 'error'); }
                    }}>Rögzítés</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── RFQ Modal ──

function RfqModal({ isOpen, onClose, partners, onSave }: {
  isOpen: boolean; onClose: () => void; partners: Partner[];
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [deadline, setDeadline] = useState('');
  const [notes, setNotes] = useState('');
  const [supplierIds, setSupplierIds] = useState<string[]>([]);
  const [lines, setLines] = useState<{ itemName: string; quantity: number; unit: string; notes: string }[]>([{ itemName: '', quantity: 1, unit: 'db', notes: '' }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) { setTitle(''); setDeadline(''); setNotes(''); setSupplierIds([]); setLines([{ itemName: '', quantity: 1, unit: 'db', notes: '' }]); }
  }, [isOpen]);

  const addLine = () => setLines([...lines, { itemName: '', quantity: 1, unit: 'db', notes: '' }]);
  const updateLine = (i: number, field: string, value: string | number) => {
    const next = [...lines];
    (next[i] as any)[field] = value;
    setLines(next);
  };
  const removeLine = (i: number) => setLines(lines.filter((_, idx) => idx !== i));

  const toggleSupplier = (id: string) => {
    setSupplierIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onSave({
      title, deadline: deadline || undefined, notes: notes || undefined,
      supplierIds, lines: lines.filter(l => l.itemName),
    });
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="+ Új ajánlatkérés">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Megnevezés *" value={title} onChange={e => setTitle(e.target.value)} required />
        <Input label="Határidő" type="date" value={deadline} onChange={e => setDeadline(e.target.value)} />
        <div className="space-y-1">
          <label >Megjegyzés</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="form-input" />
        </div>

        <div className="space-y-1">
          <label >Beszállítók — kiknek küldjük</label>
          <div className="max-h-[120px] overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
            {partners.map(p => (
              <label key={p.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer hover:bg-gray-50 px-1 rounded">
                <input type="checkbox" checked={supplierIds.includes(p.id)} onChange={() => toggleSupplier(p.id)}  />
                {p.name}
              </label>
            ))}
            {partners.length === 0 && <p className="text-xs text-gray-400">Nincs partner a rendszerben.</p>}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label >Tételek</label>
            <button type="button" onClick={addLine} className="text-xs text-[#F47B20] font-semibold hover:underline">+ Új tétel</button>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end">
              <input value={l.itemName} onChange={e => updateLine(i, 'itemName', e.target.value)} placeholder="Cikk / Megnevezés" className="col-span-5" />
              <NumInput value={l.quantity} onChange={v => updateLine(i, 'quantity', v)} min={0.001} placeholder="Menny." className="col-span-2" />
              <input value={l.unit} onChange={e => updateLine(i, 'unit', e.target.value)} placeholder="Egység" className="col-span-2" />
              <input value={l.notes} onChange={e => updateLine(i, 'notes', e.target.value)} placeholder="Megjegyzés" className="col-span-2" />
              <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-red-400 hover:text-red-600 text-sm">×</button>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>Létrehozás</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── PO Modal ──

function PoModal({ isOpen, onClose, partners, onSave }: {
  isOpen: boolean; onClose: () => void; partners: Partner[];
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const [selectedSupplier, setSelectedSupplier] = useState<{ id: string; name: string; tax_number?: string } | null>(null);
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<{ itemName: string; quantity: number; unitPrice: number; vatRate: number }[]>([{ itemName: '', quantity: 1, unitPrice: 0, vatRate: 27 }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) { setSelectedSupplier(null); setExpectedDelivery(''); setReference(''); setLines([{ itemName: '', quantity: 1, unitPrice: 0, vatRate: 27 }]); }
  }, [isOpen]);

  const addLine = () => setLines([...lines, { itemName: '', quantity: 1, unitPrice: 0, vatRate: 27 }]);
  const updateLine = (i: number, field: string, value: string | number) => {
    const next = [...lines];
    (next[i] as any)[field] = value;
    setLines(next);
  };
  const removeLine = (i: number) => setLines(lines.filter((_, idx) => idx !== i));

  const total = lines.reduce((s, l) => s + l.quantity * l.unitPrice * (1 + l.vatRate / 100), 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onSave({
      supplierId: selectedSupplier?.id, expectedDelivery: expectedDelivery || undefined,
      reference: reference || undefined,
      items: lines.filter(l => l.itemName).map(l => ({
        description: l.itemName, quantity: l.quantity, unit: 'db',
        netUnitPrice: l.unitPrice, vatRate: l.vatRate,
      })),
    });
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="+ Új beszerzési megrendelés">
      <form onSubmit={handleSubmit} className="space-y-4">
        <PartnerAutocomplete label="Beszállító" required value={selectedSupplier} onChange={setSelectedSupplier} placeholder="Keress beszállítót..." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Várt szállítás" type="date" value={expectedDelivery} onChange={e => setExpectedDelivery(e.target.value)} />
          <Input label="Hivatkozás" value={reference} onChange={e => setReference(e.target.value)} placeholder="Ajánlat-szám" />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label >Tételek</label>
            <button type="button" onClick={addLine} className="text-xs text-[#F47B20] font-semibold hover:underline">+ Új tétel</button>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end">
              <input value={l.itemName} onChange={e => updateLine(i, 'itemName', e.target.value)} placeholder="Cikk" className="col-span-4" />
              <NumInput value={l.quantity} onChange={v => updateLine(i, 'quantity', v)} min={0.001} placeholder="Menny." className="col-span-2" />
              <MoneyInput value={l.unitPrice} onChange={v => updateLine(i, 'unitPrice', v)} placeholder="Egységár" className="col-span-3" />
              <select value={l.vatRate} onChange={e => updateLine(i, 'vatRate', Number(e.target.value))} className="col-span-2 px-1 py-1.5 text-xs border border-gray-300 rounded">
                <option value={27}>27%</option><option value={18}>18%</option><option value={5}>5%</option><option value={0}>0%</option>
              </select>
              <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-red-400 hover:text-red-600 text-sm">×</button>
            </div>
          ))}
        </div>

        <div className="text-right text-sm">
          <span className="text-gray-500">Összesen (bruttó):</span>{' '}
          <span className="font-bold text-[#1a2540]">{new Intl.NumberFormat('hu-HU').format(Math.round(total))} Ft</span>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>Létrehozás</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Goods Receipt Modal ──

function GrModal({ isOpen, onClose, pos, onSave }: {
  isOpen: boolean; onClose: () => void; pos: PurchaseOrder[];
  onSave: (poId: string, data: Record<string, unknown>) => Promise<void>;
}) {
  const [poId, setPoId] = useState('');
  const [receiptDate, setReceiptDate] = useState(todayStr());
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState('');
  const [receiveAll, setReceiveAll] = useState(false);
  const [poLines, setPoLines] = useState<{ lineId: string; itemName: string; ordered: number; received: number; nowReceive: number }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) { setPoId(''); setReceiptDate(todayStr()); setDeliveryNoteNumber(''); setReceiveAll(false); setPoLines([]); }
  }, [isOpen]);

  // Load PO lines when PO selected
  useEffect(() => {
    if (!poId) { setPoLines([]); return; }
    (async () => {
      try {
        const po = await get<any>(`/procurement/orders/${poId}`);
        if (po?.lines) {
          setPoLines(po.lines.map((l: any) => ({
            lineId: l.id, itemName: l.item_name, ordered: l.quantity,
            received: l.received_qty || 0, nowReceive: l.quantity - (l.received_qty || 0),
          })));
        }
      } catch { /* silent */ }
    })();
  }, [poId]);

  useEffect(() => {
    if (receiveAll) {
      setPoLines(prev => prev.map(l => ({ ...l, nowReceive: l.ordered - l.received })));
    }
  }, [receiveAll]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!poId) return;
    setSaving(true);
    await onSave(poId, {
      receiptDate, deliveryNoteNumber: deliveryNoteNumber || undefined,
      lines: poLines.map(l => ({ poLineId: l.lineId, quantity: l.nowReceive })).filter(l => l.quantity > 0),
    });
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="+ Áru-fogadás">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label >PO *</label>
          <select value={poId} onChange={e => setPoId(e.target.value)} required className="form-input">
            <option value="">Válassz megrendelést...</option>
            {pos.map(po => <option key={po.id} value={po.id}>{po.po_number} — {po.supplier_name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Fogadás dátuma *" type="date" value={receiptDate} onChange={e => setReceiptDate(e.target.value)} required />
          <Input label="Szállítólevél száma" value={deliveryNoteNumber} onChange={e => setDeliveryNoteNumber(e.target.value)} placeholder="A beszállítótól" />
        </div>

        {poLines.length > 0 && (
          <div className="space-y-2">
            <label >Tételek</label>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs text-gray-500">
                  <th className="text-left py-1">Cikk</th>
                  <th className="text-right py-1">Megrendelt</th>
                  <th className="text-right py-1">Eddig fogadva</th>
                  <th className="text-right py-1">Most fogad</th>
                </tr>
              </thead>
              <tbody>
                {poLines.map((l, i) => (
                  <tr key={l.lineId} className="border-b border-gray-50">
                    <td className="py-2">{l.itemName}</td>
                    <td className="py-2 text-right">{l.ordered}</td>
                    <td className="py-2 text-right">{l.received}</td>
                    <td className="py-2 text-right">
                      <NumInput
                        value={l.nowReceive} min={0} max={l.ordered - l.received}
                        onChange={v => {
                          const next = [...poLines];
                          next[i].nowReceive = v;
                          setPoLines(next);
                        }}
                        className="w-16"
                        style={{ textAlign: 'right' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={receiveAll} onChange={e => setReceiveAll(e.target.checked)}  />
              Minden tétel teljes szállítás
            </label>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>Rögzítés</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Hálózati megrendelések tab (vevőként leadott network orderek) ──

interface NetworkOrder {
  id: string;
  seller_name: string;
  buyer_name: string;
  status: string;
  note: string | null;
  created_at: string;
  items: Array<{ description: string; quantity: number; unit: string; net_unit_price: number | null }>;
}

const NET_STATUS: Record<string, { label: string; color: string }> = {
  pending:   { label: 'Függőben', color: 'var(--orange)' },
  confirmed: { label: 'Visszaigazolva', color: '#0EA5E9' },
  rejected:  { label: 'Elutasítva', color: 'var(--red)' },
  fulfilled: { label: 'Teljesítve', color: 'var(--green)' },
  invoiced:  { label: 'Számlázva', color: '#8B5CF6' },
  cancelled: { label: 'Visszavonva', color: 'var(--slate-2)' },
};

function fmtFillér(v: number | null) {
  if (!v) return '—';
  return new Intl.NumberFormat('hu-HU').format(Math.round(v)) + ' Ft';
}

function NetworkOrdersTab() {
  const [orders, setOrders] = useState<NetworkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<NetworkOrder | null>(null);

  useEffect(() => {
    get<NetworkOrder[]>('/network/orders?role=buyer')
      .then(data => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-400">Betöltés...</div>;

  if (orders.length === 0) {
    return (
      <EmptyState
        icon="🌐"
        title="Még nincs hálózati megrendelés"
        description="Ha Modulum hálózati partnerektől rendelsz a katalógusból, azok itt jelennek meg."
        action={<Link to="/halozat"><Button variant="ghost" size="sm">Modulum Hálózat →</Button></Link>}
      />
    );
  }

  return (
    <>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">Eladó</th>
                <th className="px-4 py-3">Tételek</th>
                <th className="px-4 py-3">Dátum</th>
                <th className="px-4 py-3">Státusz</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold" style={{ color: 'var(--navy)' }}>{o.seller_name}</td>
                  <td className="px-4 py-3 text-gray-500">{o.items.length} tétel</td>
                  <td className="px-4 py-3 text-gray-500">{new Date(o.created_at).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}</td>
                  <td className="px-4 py-3">
                    <span style={{ fontWeight: 700, fontSize: 12, color: NET_STATUS[o.status]?.color }}>
                      {NET_STATUS[o.status]?.label || o.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setDetail(o)}
                      style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Részletek
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {detail && (
        <Modal isOpen={true} title={`Hálózati megrendelés — ${detail.seller_name}`} onClose={() => setDetail(null)}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--slate)' }}>
                {new Date(detail.created_at).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <span style={{ fontWeight: 700, fontSize: 13, color: NET_STATUS[detail.status]?.color }}>
                {NET_STATUS[detail.status]?.label}
              </span>
            </div>
            {detail.note && (
              <div style={{ background: 'var(--mist)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13, color: 'var(--slate)' }}>
                💬 {detail.note}
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--line)', color: 'var(--slate-2)', fontSize: 11 }}>
                  <th style={{ textAlign: 'left', padding: '4px 0', fontWeight: 700 }}>Tétel</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Menny.</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Egységár</th>
                </tr>
              </thead>
              <tbody>
                {detail.items.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--line-2)' }}>
                    <td style={{ padding: '7px 0', color: 'var(--navy)' }}>{item.description}</td>
                    <td style={{ textAlign: 'right', color: 'var(--slate)' }}>{item.quantity} {item.unit}</td>
                    <td style={{ textAlign: 'right', color: 'var(--slate)' }}>{fmtFillér(item.net_unit_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Link to="/halozat" style={{ fontSize: 13, color: 'var(--orange)', fontWeight: 700 }}>
                Megrendelés kezelése a Hálózatban →
              </Link>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
